"use strict";

/**
 * If updating any of these field listings, be sure to also update the corresponding
 * import template PDF so it lists the most up-to-date fields.
 **/
const ImportFields = function () {
  const catchBasinDisplayNames = {
    assetId: "Unique ID",
    catchBasinType: "Catch Basin Type",
    phase: "Phase",
    responsiblePartyMaintenance: "Maintained By",
    latitude: "Latitude",
    longitude: "Longitude",
    totalDepth: "Total Depth",
    sump: "Sump?",
    sumpDepth: "Sump Depth",
    installDate: "Install Date",
    systemAccess: "Confined Space",
    length: "Length",
    width: "Width",
    diameter: "Diameter",
    consequenceOfFailure: "Consequence Of Failure",
    assessmentFrequency: "Functional Assessment Frequency",
    inspectionFrequency: "Routine Site Visit Frequency",
    proximityTo303d: "In Proximity to 303d",
    dryWeatherMonitoringLocation: "Dry Weather Monitoring Location",
    dryWeatherScreeningFrequencyMonths: "Dry Weather Screening Frequency (months)",
    fullCaptureSystem: "Full Capture System?",
    drainageArea: "Drainage Area",
    imperviousArea: "Impervious Area Treated",
    structureId: "Structure ID",
    projectId: "Associated Project",
    facilityId: "Associated Facility",
    inletType: "Catch Basin Inlet Type",
    siphon: "Siphon",
    footprintMaterial: "Footprint Material",
    wallMaterial: "Wall Material",
    assetShape: "Catch Basin Shape",
    invertElevation: "Invert Elevation",
    baseElevation: "Base Elevation",
    rimElevation: "Rim Elevation",
    datum: "Vertical Datum",
    manufacturer: "Manufacturer",
    model: "Name/Model",
    property: "Land Ownership",
    responsiblePartyDelivery: "Delivered By",
    address: "Street Address",
    city: "City",
    state: "State",
    zip: "Zip Code",
    notes: "Notes",
  };

  const sbmpsDisplayNames = {
    assetId: "Unique ID",
    bmpType: "BMP Type",
    phase: "Phase",
    responsiblePartyMaintenance: "Maintained By",
    latitude: "Latitude",
    longitude: "Longitude",
    installDate: "Install Date",
    systemAccess: "Confined Space",
    consequenceOfFailure: "Consequence Of Failure",
    proximityTo303d: "In Proximity to 303d",
    inletQuantity: "Inlets",
    outletQuantity: "Outlets",
    visibleScreen: "Visible Screen at Outlet",
    assessmentFrequency: "Functional Assessment Frequency",
    inspectionFrequency: "Routine Site Visit Frequency",
    certAssessmentFrequency: "Certification Inspection Frequency",
    dryWeatherMonitoringLocation: "Dry Weather Monitoring Location",
    dryWeatherScreeningFrequencyMonths: "Dry Weather Screening Frequency (months)",
    footprint: "Footprint",
    capacity: "Treatment Capacity",
    maintenanceInterval: "Closed Space Maintenance Interval",
    invStwat: "Observation Ports for Standing Water",
    invSedacc: "Observation Ports for Material Accumulation",
    totalDepth: "Total Depth",
    fullCaptureSystem: "Full Capture System?",
    lowflow: "Low Flow Bypass",
    diversion: "Dry Weather Diversion",
    drainsout: "Capture & Reuse",
    flowpath: "Flow Path Length to BMP (ft)",
    drainageArea: "Drainage Area",
    imperviousArea: "Impervious Area Treated",
    treatmentRate: "Treatment Rate",
    wetPoolCapacity: "Wet Pool Capacity",
    infiltrationRate: "Infiltration Rate",
    drawdownTime: "Drawdown Time",
    intakeRate: "Intake Rate",
    structureId: "Structure ID",
    projectId: "Associated Project",
    facilityId: "Associated Facility",
    footprintMaterial: "Footprint Material",
    wallMaterial: "Wall Material",
    treatedStormType: "Design Storm Type",
    treatedStormPercentile: "Design Storm Percentile",
    treatedStormDepth: "Design Storm Depth",
    invertElevation: "Invert Elevation",
    baseElevation: "Base Elevation",
    rimElevation: "Rim Elevation",
    datum: "Vertical Datum",
    manufacturer: "Manufacturer",
    model: "Name/Model",
    property: "Land Ownership",
    responsiblePartyDelivery: "Delivered By",
    filterQuantity: "Number of Filters",
    mediaType: "Media Type",
    filterThickness: "Thickness",
    screenCount: "Number of Screens",
    address: "Street Address",
    city: "City",
    state: "State",
    zip: "Zip Code",
    notes: "Notes",
  };

  const fcssDisplayNames = {
    multiSystem: "FCS Grouping",
    volumeBased: "FCS Function",
    trapsParticles: "Trap 5mm Particles",
    treatsStorm: "Treat 1yr 1hr Storm",
    stormInches: "1yr 1hr Storm",
    partialStormInches: "Treated Storm",
    stateApproved: "FCS State Approved Type",
    fcsInspectFreq: "Trash FCS Inspection Frequency",
  };

  const bmpObservationsDisplayNames = {
    bmpId: "BMP Unique ID",
    observationDate: "Date",
    condition: "Condition",
  };

  const outfallsDisplayNames = {
    assetId: "Unique ID",
    rotationAzimuth: "Azimuth Direction",
    latitude: "Latitude",
    longitude: "Longitude",
    drainageAreaAcres: "Drainage Area",
    diameter: "Diameter",
    width: "Width of Pipe",
    height: "Length of Pipe",
    invertElevation: "Outlet Invert Elevation",
    baseElevation: "Outlet Base Elevation",
    rimElevation: "Outlet Rim Elevation",
    outletType: "Outlet Type",
    outfallType: "Type of Outfall",
    assetShape: "Shape of Outfall",
    assetMaterial: "Material of Outfall",
    consequenceOfFailure: "Consequence Of Failure",
    notes: "Notes",
    installDate: "Install Date",
    datum: "Vertical Datum",
    responsiblePartyMaintenance: "Maintained By",
    phase: "Phase",
    structureId: "Structure ID",
    projectId: "Associated Project",
    facilityId: "Associated Facility",
    confinedSpace: "Confined Space",
    property: "Property",
    responsiblePartyDelivery: "Delivered By",
    dryWeatherMonitoringLocation: "Dry Weather Screening Location",
    dryWeatherScreeningFrequencyMonths: "Dry Weather Screening Frequency (months)",
    wqMonitoringLocation: "WQ Monitoring Location",
    locationPhase: "Monitoring Location Phase",
    locationActiveDate: "Active Date",
    locationInactiveDate: "Inactive Date",
    address: "Address",
    city: "City",
    state: "State",
    zip: "Zip",
    routineSiteVisitFrequencyMonths: "Routine Site Visit Frequency",
  };

  const projectsDisplayNames = {
    // Applies to All Projects
    projectIdentifier: "Project ID",
    projectName: "Project Name",
    latitude: "Latitude",
    longitude: "Longitude",
    streetAddress: "Address",
    city: "City",
    state: "State",
    zipCode: "ZIP Code",
    implementerType: "Delivered By",
    maintainerType: "Maintained By",
    highPriority: "Priority",
    priorityRationale: "Priority Rationale",
    developmentType: "Development Type",
    briefDescription: "Brief Project Description",
    areaOfDisturbance: "Area of Disturbance (acres)",
    manualArea: "Total Manual Project Area (acres)",
    netImperviousArea: "Net Impervious Area (acres)",
    preImperviousArea: "Pre-Project Impervious Area (acres)",
    postImperviousArea: "Post-Project Impervious Area (acres)",
    proximityToReceivingWater: "Proximity to Receiving Waters (feet)",
    proximityTo303dReceivingWater: "Proximity to 303(d) Receiving Waters (feet)",
    phase: "Project Phase",
    has303dPollutant: "303d Pollutant",
    adjacentToEsa: "Adjacent to ESA",
    initialMaintenanceNotice: "Initial Maintenance Notice",
    initialRoutineInspection: "Initial Routine Inspection",

    // Applies to Compliance Projects
    projectStartDate: "Construction Start",
    projectEndDate: "Construction End",
    projectDeliveryCost: "Delivery Cost",
    projectType: "Project Type",
    averageSiteSlopePercent: "Average Site Slope (%)",
    erosiveSiteSoils: "Erosive Site Soils",
    nonstormwaterDischarge: "Non-Stormwater Discharge",
    generalPermitCoverage: "CGP Coverage",
    wasteDischargerId: "Permit/WDID Number",
    cgpRiskLevel: "CGP Risk Level",
    linearUndergroundProject: "Linear Underground Project",
    erosivityWaiver: "Erosivity Waiver",
    offsiteMitigation: "Off-Site Mitigation",
    relatedProject: "Off-Site Related Project ID",
    inLieuFees: "In-Lieu Fees",
    inLieuFeeAmount: "In-Lieu Fee Amount",
    alternativeCompliance: "Alternative Compliance",

    exempt: "Tier: Exempt",
    siteDesign: "Tier: Site Design",
    wqTreatment: "Tier: WQ Treatment",
    runoffRetention: "Tier: Runoff Retention",
    peakManagement: "Tier: Peak Management",
    exemptionDescription: "Exemption Rationale",
    contaminatedSoils: "Special Circumstance: Contaminated Soils",
    contiguousToWetland: "Special Circumstance: Contiguous to Wetland",
    femaArea: "Special Circumstance: FEMA Area",
    highlyAlteredChannel: "Special Circumstance: Highly Altered Channel",
    historicLakeOrWetland: "Special Circumstance: Historic Lake or Wetland",
    intermediateFlowControl: "Special Circumstance: Intermediate Flow Control",
    infeasibilityWqTreatment: "Tech. Infeasibility: WQ Treatment",
    infeasibilityRunoffRetention: "Tech. Infeasibility: Runoff Retention",
    infeasibilityPeakManagement: "Tech. Infeasibility: Peak Management",
    watershedOrRegionPlan: "Alt. Compliance: Watershed or Regional Plan",
    urbanSustainabilityArea: "Alt. Compliance: Urban Sustainability Area",
    otherDescription: "Alt. Compliance: Other description",

    // Applies to Project Delivery
    landOwnership: "Land Ownership",
    budgetBookId: "Budget Book ID",
    rejectionReason: "Shelved/Rejection Reason",

    // Improvement Types
    improvementType: "Improvement Type",
    improvementBmpType: "Improvement BMP Type",
    improvementEnvironmentalSiteDesignType: "Improvement Environmental Site Design Type",
    improvementRiparianRestorationType: "Riparian Restoration Type",
    improvementCount: "Improvement Count",
    improvementCapacityAcreFeet: "Improvement Capacity (ac-ft)",
    improvementImperviousAreaAcres: "Treated Impervous Area (ac)",
    improvementFootprintSquareFeet: "Improvement Footprint (sqft)",
    improvementLengthFeet: "Length (ft)",
  };

  const projectContactsDisplayNames = {
    projectIdentifier: "Project Identifier",
    firstName: "First Name",
    lastName: "Last Name",
    role: "Role",
    phoneNumber: "Phone Number",
    emailAddress: "Email Address",
    mailingAddress: "Street Address",
    city: "City",
    state: "State",
    zipCode: "Zip Code",
    affiliation: "Affiliation",
    isInactive: "Inactive?",
    companyName: "Company Name",
  };
  const facilityContactsDisplayNames = {
    facilityIdentifier: "Facility Identifier",
    firstName: "First Name",
    lastName: "Last Name",
    role: "Role",
    phoneNumber: "Phone Number",
    emailAddress: "Email Address",
    mailingAddress: "Street Address",
    city: "City",
    state: "State",
    zipCode: "Zip Code",
    affiliation: "Affiliation",
    isInactive: "Inactive?",
    companyName: "Company Name",
  };

  const facilitiesDisplayNames = {
    facilityIdentifier: "Facility ID",
    latitude: "Latitude",
    longitude: "Longitude",
    facilityName: "Facility Name",
    facilityType: "Facility Type",
    facilityPhase: "Facility Phase",
    responsiblePartyManagement: "Responsible Party",
    sicCode: "SIC Code",
    isIndustrialGeneralPermit: "Registered Under IGP",
    industrialGeneralPermitNumber: "Industrial General Permit Number",
    wasteDischargerIdentifier: "Permit/WDID Number",
    fogParticipant: "FOG Participant",
    riskProfile: "Risk Profile",
    exemptionNumber: "Exemption Number",
    openedDate: "Facility Open Date",
    closedDate: "Facility Closed Date",
    inspectionFrequencyMonths: "Inspection Frequency",
    address: "Street Address",
    city: "City",
    state: "State",
    zip: "ZIP Code",
    has303dPollutant: "303d Pollutant",
    adjacentToEsa: "Adjacent to ESA",
    mobileBusiness: "Mobile Business",
    epcraFacility: "EPCRA Facility",
    notes: "Notes",
  };

  const drainageDisplayNames = {
    asset_id: "Asset ID",
    geometry: "Geometry",
  };

  const projectAreaDisplayNames = {
    projectIdentifier: "Project Identifier",
    geometry: "Geometry",
  };

  const zonesDisplayNames = {
    identifier: "Unique Identifier",
    name: "Zone Name",
    geometry: "Geometry",
  };

  const openChannelsDisplayNames = {
    geometry: "Geometry",
    asset_id: "Asset ID",
    structure_id: "Structure ID",
    open_channel_type: "Channel Type",
    service_type: "Line Service Type",
    flow_type: "Flow Type",
    asset_material: "Material",
    asset_shape: "Shape",
    width: "Width",
    slope: "Slope",
    datum: "Elevation Datum",
    flow_direction: "Flow Direction",
    depth: "Channel Design Depth",
    confined_space: "Confined Space",
    height: "Height",
    linear_length: "Linear Length",
    area: "Area",
    upstream_elevation: "Upstream Elevation",
    downstream_elevation: "Downstream Elevation",
    responsible_party_delivery: "Delivered By",
    responsible_party_maintenance: "Maintained By",
    install_date: "Install Date",
    phase: "Phase",
    lined: "Lined?",
    lining_method: "Lining Method",
    lining_year: "Lining Year",
    accessible: "Accessible?",
    consequence_of_failure: "Consequence of Failure",
    dry_weather_monitoring_location: "Dry Weather Monitoring Location",
    dry_weather_screening_frequency_months: "Dry Weather Screening Frequency (months)",
    property: "Land Ownership",
    upstream_node: "Upstream Node",
    downstream_node: "Downstream Node",
    city: "City",
    state: "State",
    zip: "Zip Code",
    notes: "Notes",
  };

  const cleanoutsDisplayNames = {
    geometry: "Geometry",
    asset_id: "Asset ID",
    structure_id: "Structure ID",
    cleanout_type: "Cleanout Type",
    cover_type: "Cover Type",
    cone_type: "Cone Type",
    frame_material: "Frame Material",
    wall_material: "Wall Material",
    surface_material: "Surface Material",
    cover_shape: "Cover Shape",
    confined_space: "Confined Space",
    length: "Length",
    width: "Width",
    diameter: "Diameter",
    invert_elevation: "Invert Elevation",
    base_elevation: "Base Elevation",
    rim_elevation: "Rim Elevation",
    datum: "Elevation Datum",
    rotation_azimuth: "Rotation Azimuth",
    depth: "Depth",
    responsible_party_delivery: "Delivered By",
    responsible_party_maintenance: "Maintained By",
    install_date: "Install Date",
    phase: "Phase",
    property: "Land Ownership",
    consequence_of_failure: "Consequence of Failure",
    dry_weather_monitoring_location: "Dry Weather Monitoring Location",
    dry_weather_screening_frequency_months: "Dry Weather Screening Frequency (months)",
    address: "Address",
    city: "City",
    state: "State",
    zip: "Zip",
    notes: "Notes",
  };

  const pipesDisplayNames = {
    geometry: "Geometry",
    asset_id: "Asset ID",
    structure_id: "Structure ID",
    pipe_type: "Pipe Type",
    service_type: "Line Service Type",
    asset_material: "Material",
    asset_shape: "Shape",
    width: "Width",
    diameter: "Diameter",
    slope: "Slope",
    datum: "Elevation Datum",
    flow_direction: "Flow Direction",
    confined_space: "Confined Space",
    height: "Height",
    linear_length: "Linear Length",
    upstream_elevation: "Upstream Elevation",
    downstream_elevation: "Downstream Elevation",
    responsible_party_delivery: "Delivered By",
    responsible_party_maintenance: "Maintained By",
    install_date: "Install Date",
    phase: "Phase",
    lined: "Lined?",
    lining_method: "Lining Method",
    lining_year: "Lining Year",
    accessible: "Accessible?",
    dry_weather_monitoring_location: "Dry Weather Monitoring Location",
    dry_weather_screening_frequency_months: "Dry Weather Screening Frequency (months)",
    property: "Land Ownership",
    upstream_node: "Upstream Node",
    downstream_node: "Downstream Node",
    city: "City",
    state: "State",
    zip: "Zip Code",
    notes: "Notes",
  };

  const liftStationsDisplayNames = {
    geometry: "Geometry",
    asset_id: "Asset ID",
    structure_id: "Structure ID",
    lift_station_type: "Lift Station Type",
    service_type: "Service Type",
    asset_material: "Material",
    length: "Length",
    width: "Width",
    sump: "Sump?",
    sump_elevation: "Sump Elevation",
    invert_elevation: "Invert Elevation",
    base_elevation: "Base Elevation",
    rim_elevation: "Rim Elevation",
    outlet_elevation: "Outlet Elevation",
    datum: "Elevation Datum",
    rotation_azimuth: "Rotation Azimuth",
    sump_depth: "Sump Depth",
    depth: "Depth",
    design_volume: "Design Volume",
    storage_volume: "Storage Volume",
    drainage_area: "Drainage Area",
    confined_space: "Confined Space",
    responsible_party_delivery: "Delivered By",
    responsible_party_maintenance: "Maintained By",
    install_date: "Install Date",
    phase: "Phase",
    property: "Land Ownership",
    consequence_of_failure: "Consequence of Failure",
    dry_weather_monitoring_location: "Dry Weather Monitoring Location",
    dry_weather_screening_frequency_months: "Dry Weather Screening Frequency (months)",
    address: "Address",
    city: "City",
    state: "State",
    zip: "Zip",
    notes: "Notes",
  };

  const manholesDisplayNames = {
    manholeName: "Asset ID",
    structureIdentifier: "Structure ID",
    latitude: "Latitude",
    longitude: "Longitude",
    manholeType: "Manhole Type",
    coverType: "Cover Type",
    coneType: "Cone Type",
    frameMaterial: "Frame Material",
    wallMaterial: "Wall Material",
    surfaceMaterial: "Surface Material",
    coverShape: "Cover Shape",
    confinedSpace: "Confined Space",
    manholeLength: "Length",
    manholeWidth: "Width",
    manholeDiameter: "Diameter",
    invertElevation: "Invert Elevation",
    baseElevation: "Base Elevation",
    rimElevation: "Rim Elevation",
    datum: "Elevation Datum",
    depth: "Junction Depth",
    property: "Land Ownership",
    implementerType: "Delivered By",
    responsiblePartyMaintenance: "Maintained By",
    installDate: "Install Date",
    phase: "Phase",
    cofIndividualOverrideText: "Consequence of Failure",
    dryWeatherMonitoringLocation: "Dry Weather Monitoring Location",
    dryWeatherScreeningFrequencyMonths: "Dry Weather Screening Frequency (months)",
    address: "Street Address",
    city: "City",
    state: "State",
    zipCode: "Zip Code",
    notes: "Notes",
  };

  const culvertsDisplayNames = {
    geometry: "Geometry",
    asset_id: "Asset ID",
    structure_id: "Structure ID",
    culvert_type: "Culvert Type",
    service_type: "Line Service Type",
    asset_material: "Material",
    asset_shape: "Shape",
    width: "Width",
    diameter: "Diameter",
    slope: "Slope",
    datum: "Elevation Datum",
    flow_direction: "Flow Direction",
    depth: "Channel Design Depth",
    confined_space: "Confined Space",
    height: "Height",
    linear_length: "Linear Length",
    upstream_elevation: "Upstream Elevation",
    downstream_elevation: "Downstream Elevation",
    responsible_party_delivery: "Delivered By",
    responsible_party_maintenance: "Maintained By",
    install_date: "Install Date",
    phase: "Phase",
    wall: "Wall?",
    lined: "Lined?",
    lining_method: "Lining Method",
    lining_year: "Lining Year",
    accessible: "Accessible?",
    consequence_of_failure: "Consequence of Failure",
    dry_weather_monitoring_location: "Dry Weather Monitoring Location",
    dry_weather_screening_frequency_months: "Dry Weather Screening Frequency (months)",
    property: "Land Ownership",
    upstream_node: "Upstream Node",
    downstream_node: "Downstream Node",
    city: "City",
    state: "State",
    zip: "Zip Code",
    notes: "Notes",
  };

  const samplingSitesDisplayNames = {
    siteIdentifier: "Site ID",
    latitude: "Latitude",
    longitude: "Longitude",
    siteName: "Site Name",
    siteType: "Site Type",
    maintainerType: "Maintained By",
    dryWeatherMonitoringLocation: "Dry Weather Screening Location",
    dryWeatherScreeningFrequencyMonths: "Dry Weather Screening Frequency (months)",
    wqMonitoringLocation: "WQ Monitoring Location",
    sitePhase: "Site Phase",
    activeDate: "Site Active Date",
    inactiveDate: "Site Inactive Date",
    address: "Street Address",
    city: "City",
    state: "State",
    zip: "Zip Code",
    notes: "Notes",
  };

  const getDisplayNameMapsByLayer = function () {
    return {
      outfalls: outfallsDisplayNames,
      catchBasins: catchBasinDisplayNames,
      sbmps: sbmpsDisplayNames,
      fcss: fcssDisplayNames,
      "bmp-observations": bmpObservationsDisplayNames,
      projects: projectsDisplayNames,
      "project-contacts": projectContactsDisplayNames,
      facilities: facilitiesDisplayNames,
      "facility-contacts": facilityContactsDisplayNames,
      drainage: drainageDisplayNames,
      zones: zonesDisplayNames,
      "project-area": projectAreaDisplayNames,
      "open-channels": openChannelsDisplayNames,
      culverts: culvertsDisplayNames,
      cleanouts: cleanoutsDisplayNames,
      pipes: pipesDisplayNames,
      "lift-stations": liftStationsDisplayNames,
      manholes: manholesDisplayNames,
      samplingSites: samplingSitesDisplayNames,
    };
  };

  var getFieldNames = function (layer) {
    const displayNameMapsByLayer = getDisplayNameMapsByLayer();
    if (layer in displayNameMapsByLayer) {
      return Object.keys(displayNameMapsByLayer[layer]);
    }
    throw `Fields with names not known for layer ${layer}.`;
  };

  var getFieldObjs = function (importFieldNames, fieldMapping, layer) {
    const displayNameMapsByLayer = getDisplayNameMapsByLayer();
    if (!(layer in displayNameMapsByLayer)) {
      throw `Fields with names not known for layer ${layer}.`;
    }
    return importFieldNames.map((importFieldName) => {
      const mappedFieldName =
        importFieldName in fieldMapping ? fieldMapping[importFieldName] : importFieldName;
      const displayName = displayNameMapsByLayer[layer][mappedFieldName];
      return {
        importFieldName: importFieldName,
        finalFieldName: mappedFieldName,
        displayName: displayName || importFieldName,
      };
    });
  };

  return {
    getFieldNames,
    getFieldObjs,
  };
};

module.exports = ImportFields();
